import { inRange } from 'lodash';

import {
  average,
  cosineSimilarity,
  distance,
  frechetDist,
  length,
  normalizeCurve,
  Point,
  rotate,
  subtract,
} from '../../../../utils/geometry';

const AVG_DIST_THRESHOLD = 400; // bigger = more lenient
const COSINE_SIMILARITY_THRESHOLD = 0; // -1 to 1, smaller = more lenient
const START_AND_END_DIST_THRESHOLD = 120; // bigger = more lenient
const FRECHET_THRESHOLD = 0.5; // bigger = more lenient
const LEN_THRESHOLD = 0.2; // bigger = more lenient
const DOT_LENGTH = 80; // largest length the dot can be (STROKE_i)

export interface Segment {
  path: string;
  start: number;
  points: Point[];
  length: number;
}

export class FontStroke {
  points: Point[];
  length: number;
  segments: Segment[];
  component: number;

  constructor(
    path: string | string[],
    points: [number, number][] | [number, number][][],
    component: number,
  ) {
    // Normalize path and points
    const _path = Array.isArray(path) ? path : [path];
    const _points = Array.isArray(points[0]?.[0]) ? points : [points];

    // Convert points to Point type and flatten
    this.points = _points
      .map((pointList) =>
        (pointList as [number, number][]).map(([x, y]) => ({ x, y })),
      )
      .flat();
    this.length = length(this.points);
    this.segments = _path.map((p, i) => {
      if (i === 0) {
        return {
          path: p,
          start: 0,
          points: this.points,
          length: this.length,
        };
      }

      // segment points should be start 1 points before segment and
      // end at the last point of the stroke
      const segmentPoints = this.points.slice(
        -(_points.slice(i).flat().length + 1),
      );
      const segmentLength = length(segmentPoints);
      return {
        path: p,
        start: 1 - segmentLength / this.length,
        points: segmentPoints,
        length: segmentLength,
      };
    });
    this.component = component;
  }

  getDistance(point: Point): number {
    return Math.min(...this.points.map((p) => distance(p, point)));
  }

  getAverageDistance(points: Point[]): number {
    return (
      points.reduce((acc, cur) => acc + this.getDistance(cur), 0) /
      points.length
    );
  }

  matchStartAndEnd(points: Point[], leniency: number): boolean {
    // console.log(
    //   'matchStartAndEnd',
    //   leniency,
    //   distance(this.points[0], points[0]) / leniency,
    //   distance(this.points[this.points.length - 1], points[points.length - 1]) /
    //     leniency,
    // );
    return (
      distance(this.points[0] as Point, points[0] as Point) <=
        START_AND_END_DIST_THRESHOLD * leniency &&
      distance(
        this.points[this.points.length - 1] as Point,
        points[points.length - 1] as Point,
      ) <=
        START_AND_END_DIST_THRESHOLD * leniency
    );
  }

  matchLength(points: Point[], leniency: number): boolean {
    const threshold = LEN_THRESHOLD + 0.4 * Math.exp(-this.length / 200);
    // console.log(
    //   'matchLength',
    //   threshold,
    //   (1 - (length(points) + 25) / (this.length + 25)) / leniency,
    // );
    return inRange(
      (1 - (length(points) + 25) / (this.length + 25)) / leniency,
      -threshold,
      threshold,
    );
  }

  matchDirection(points: Point[]): boolean {
    const getEdgeVectors = (p: Point[]) => {
      const vectors: Point[] = [];
      let lastPoint = p[0];
      p.slice(1).forEach((point) => {
        vectors.push(subtract(point, lastPoint as Point));
        lastPoint = point;
      });
      return vectors;
    };

    const edgeVectors = getEdgeVectors(points);
    const strokeVectors = getEdgeVectors(this.points);
    const similarities = edgeVectors.map((edgeVector) => {
      const strokeSimilarities = strokeVectors.map((strokeVector) =>
        cosineSimilarity(strokeVector, edgeVector),
      );
      return Math.max(...strokeSimilarities);
    });
    const avgSimilarity = average(similarities);
    // console.log('matchDirection', COSINE_SIMILARITY_THRESHOLD, avgSimilarity);
    return avgSimilarity > COSINE_SIMILARITY_THRESHOLD;
  }

  matchShape(points: Point[], leniency: number): boolean {
    const normCurve1 = normalizeCurve(points);
    const normCurve2 = normalizeCurve(this.points);

    let minDist = Infinity;
    const matching = [
      Math.PI / 8,
      Math.PI / 16,
      Math.PI / 32,
      0,
      (-1 * Math.PI) / 32,
      (-1 * Math.PI) / 16,
      (-1 * Math.PI) / 8,
    ];
    if (this.length > 100) {
      matching.splice(0, 1);
      matching.pop();
    }
    if (this.length > 200) {
      matching.splice(0, 1);
      matching.pop();
    }
    if (this.length > 300) {
      matching.splice(0, 1);
      matching.pop();
    }
    matching.forEach((theta) => {
      const dist = frechetDist(normCurve1, rotate(normCurve2, theta));
      if (dist < minDist) {
        minDist = dist;
      }
    });
    // console.log('matchShape', FRECHET_THRESHOLD, minDist / leniency);
    return minDist <= FRECHET_THRESHOLD * leniency;
  }

  getMatchData(
    points: Point[],
    {
      distanceLeniency = 1,
      lengthLeniency = 1,
      shapeLeniency = 1,
      showHint = false,
    },
  ) {
    const avgDist = this.getAverageDistance(points);
    const distMod = showHint ? 0.5 : 1;
    const withinDistThresh =
      avgDist <= AVG_DIST_THRESHOLD * distMod * distanceLeniency;
    // short circuit for faster matching
    // console.log(
    //   'withinDistThresh',
    //   AVG_DIST_THRESHOLD,
    //   avgDist / distMod / distanceLeniency,
    // );
    if (!withinDistThresh) {
      return { isMatch: false, avgDist };
    }
    const isDot =
      this.points.length === 2 &&
      this.length <= DOT_LENGTH &&
      length(points) <= DOT_LENGTH;
    const startAndEndMatch = this.matchStartAndEnd(points, distanceLeniency);
    const lengthMatch = isDot || this.matchLength(points, lengthLeniency);
    const directionMatch = isDot || this.matchDirection(points);
    const shapeMatch = isDot || this.matchShape(points, shapeLeniency);
    // console.log({
    //   withinDistThresh,
    //   startAndEndMatch,
    //   lengthMatch,
    //   directionMatch,
    //   shapeMatch,
    // });
    return {
      isMatch:
        withinDistThresh &&
        startAndEndMatch &&
        lengthMatch &&
        directionMatch &&
        shapeMatch,
      avgDist,
    };
  }
}
